"use client";

import { createPortal } from "react-dom";
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react";
import { isTopLayer, registerLayer } from "./layer-stack";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not(:disabled)",
  "input:not(:disabled):not([type=hidden])",
  "select:not(:disabled)",
  "textarea:not(:disabled)",
  "iframe",
  "object",
  "embed",
  "[contenteditable=\"true\"]:not([inert])",
  "[tabindex]:not([tabindex=\"-1\"]):not(:disabled)",
].join(",");

export type OverlayFocusTarget =
  | "first"
  | false
  | HTMLElement
  | RefObject<HTMLElement | null>
  | (() => HTMLElement | null);

export type OverlayProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  portal?: boolean;
  closeOnEscape?: boolean;
  closeOnBackdrop?: boolean;
  lockScroll?: boolean;
  returnFocus?: boolean;
  initialFocus?: OverlayFocusTarget;
  overlayClassName?: string;
  backdropClassName?: string;
  panelClassName?: string;
  role?: "dialog" | "alertdialog";
  ariaModal?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
};

let scrollLockCount = 0;
let previousBodyOverflow = "";
let previousBodyPaddingRight = "";

function joinClassNames(...classNames: Array<string | undefined>) {
  return classNames.filter(Boolean).join(" ");
}

function focusElement(element: HTMLElement | null) {
  if (!element) return;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

function focusableElements(root: HTMLElement) {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
    if (element.matches(":disabled") || element.closest("[hidden], [aria-hidden=\"true\"], [inert]")) return false;

    const style = element.ownerDocument.defaultView?.getComputedStyle(element);
    if (style && (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse")) return false;

    const rect = element.getClientRects()[0];
    return Boolean(rect && rect.width > 0 && rect.height > 0);
  });
}

function resolveFocusTarget(target: OverlayFocusTarget | undefined) {
  if (!target || target === "first") return null;
  if (typeof target === "function") return target();
  if ("current" in target) return target.current;
  return target;
}

function lockBodyScroll() {
  if (typeof document === "undefined") return () => undefined;

  if (scrollLockCount === 0) {
    previousBodyOverflow = document.body.style.overflow;
    previousBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  scrollLockCount += 1;
  let released = false;

  return () => {
    if (released) return;
    released = true;
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount !== 0) return;

    document.body.style.overflow = previousBodyOverflow;
    document.body.style.paddingRight = previousBodyPaddingRight;
  };
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}

export const Overlay = forwardRef<HTMLDivElement, OverlayProps>(function Overlay(
  {
    open,
    onOpenChange,
    children,
    portal = true,
    closeOnEscape = true,
    closeOnBackdrop = true,
    lockScroll = true,
    returnFocus = true,
    initialFocus,
    overlayClassName,
    backdropClassName,
    panelClassName,
    role,
    ariaModal,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
  },
  forwardedRef,
) {
  const [mounted, setMounted] = useState(false);
  const layerId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const backdropPointerRef = useRef<"handled" | "blocked" | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const onOpenChangeRef = useRef(onOpenChange);
  const closeOnEscapeRef = useRef(closeOnEscape);
  const closeOnBackdropRef = useRef(closeOnBackdrop);
  const lockScrollRef = useRef(lockScroll);
  const returnFocusRef = useRef(returnFocus);
  const initialFocusRef = useRef(initialFocus);

  onOpenChangeRef.current = onOpenChange;
  closeOnEscapeRef.current = closeOnEscape;
  closeOnBackdropRef.current = closeOnBackdrop;
  lockScrollRef.current = lockScroll;
  returnFocusRef.current = returnFocus;
  initialFocusRef.current = initialFocus;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open || (portal && !mounted)) return;
    return registerLayer(layerId);
  }, [layerId, mounted, open, portal]);

  useEffect(() => {
    if (!open || (portal && !mounted) || typeof document === "undefined") return;

    const root = rootRef.current;
    if (!root) return;

    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const unlockScroll = lockScrollRef.current ? lockBodyScroll() : undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (!isTopLayer(layerId)) return;
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (!closeOnEscapeRef.current) return;
        onOpenChangeRef.current(false);
        return;
      }

      if (event.key !== "Tab" || !isTopLayer(layerId)) return;

      const focusable = focusableElements(root);
      if (focusable.length === 0) {
        event.preventDefault();
        focusElement(root.querySelector<HTMLElement>("[data-fz-overlay-panel]"));
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (!root.contains(activeElement)) {
        event.preventDefault();
        focusElement(event.shiftKey ? last : first);
        return;
      }

      if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        focusElement(last);
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        focusElement(first);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    const focusTimer = window.setTimeout(() => {
      if (initialFocusRef.current === false) return;
      const explicitTarget = resolveFocusTarget(initialFocusRef.current);
      if (explicitTarget?.isConnected) {
        focusElement(explicitTarget);
        return;
      }
      focusElement(focusableElements(root)[0] ?? root.querySelector<HTMLElement>("[data-fz-overlay-panel]"));
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      unlockScroll?.();

      const elementToRestore = restoreFocusRef.current;
      restoreFocusRef.current = null;
      if (returnFocusRef.current && elementToRestore?.isConnected) focusElement(elementToRestore);
    };
  }, [layerId, mounted, open, portal]);

  if (!open || (portal && !mounted)) return null;

  const content = (
    <div ref={rootRef} className={overlayClassName} data-fz-overlay-root>
      <div
        aria-hidden="true"
        className={joinClassNames("absolute inset-0 bg-[var(--fz-overlay-backdrop)]", backdropClassName)}
        data-fz-overlay-backdrop
        onPointerDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (!isTopLayer(layerId)) {
            backdropPointerRef.current = "blocked";
            return;
          }
          backdropPointerRef.current = "handled";
          event.preventDefault();
          event.stopPropagation();
          if (!closeOnBackdropRef.current) return;
          onOpenChangeRef.current(false);
        }}
        onClick={(event) => {
          if (event.target !== event.currentTarget) return;
          const pointerState = backdropPointerRef.current;
          backdropPointerRef.current = null;
          if (pointerState) return;
          if (!isTopLayer(layerId) || !closeOnBackdropRef.current) return;
          onOpenChangeRef.current(false);
        }}
      />
      <div
        ref={(element) => assignRef(forwardedRef, element)}
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-modal={ariaModal ? "true" : undefined}
        className={panelClassName}
        data-fz-overlay-panel
        role={role}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );

  if (portal && typeof document !== "undefined") return createPortal(content, document.body);
  return content;
});
